// Proposing changes to a room somebody else is playing.
//
// Everything here is shaped by one fact: the room rejects, it never transforms.
// So a proposal either lands whole or comes back with the value the room
// actually holds, and the honest thing to do with a rejection is show it and
// re-read — never retry, which is how a stale write becomes a loop.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Intent, IntentEnvelope, IntentResponse, Rejection } from "@shared/sync/intents.js";
import { cloneDevices, isChainOwner, type CanonicalRoom, type DeviceState } from "@shared/sync/state.js";
import { postIntents } from "@web-shared/lib/api";
import { coalesceIntents } from "@web-shared/lib/edits";
import {
  answerOverlays,
  dropOverlays,
  holdOverlays,
  liveOverlays,
  overlaysOf,
  projectClips,
  type PendingEdit,
} from "@web-shared/lib/edit-overlay";
import { rebaseIntents } from "@web-shared/lib/rebase-intents";
import { describeIntents, rebornIds, recordUndo, remapEntry, undoPlan, type UndoEntry } from "@web-shared/lib/undo";
import type { RoomReplica } from "@web-shared/hooks/useRoomReplica";

/** Which stack a proposal's inverse lands on. A fresh edit pushes onto undo
 * (and empties redo — the branch it would have replayed is gone); an undo
 * pushes its inverse onto redo; a redo pushes back onto undo. Work the page does
 * by itself is on no stack: it is not the musician's to undo, and the page would
 * only do it again, so Undo could never get past it. */
type History = "push" | "undo" | "redo" | "none";

const MAX_HISTORY = 100;
/** Undo/redo keypresses held while an edit is in flight. */
const MAX_QUEUED = 20;

export interface RejectionNote extends Rejection {
  at: string;
}

export interface JamEditor {
  /** The room as we believe it will be, with in-flight edits folded in. */
  room: CanonicalRoom | null;
  /** Clip ids currently awaiting the room's verdict. */
  pendingClipIds: Set<string>;
  rejections: RejectionNote[];
  dismissRejections: () => void;
  busy: boolean;
  error: string | null;
  /** Resolves to the room's answer (null when nothing was sent), so a caller
   * that follows up on an accepted gesture can stop when it was refused. */
  propose: (intents: Intent[]) => Promise<IntentResponse | null>;
  /** The same, for what the page does by itself (a render): nothing to undo. */
  proposeUnrecorded: (intents: Intent[]) => Promise<IntentResponse | null>;
  /** Editing in reverse — see lib/undo.ts. Unavailable while an edit is still
   * in flight: its inverse would cite revs the room is about to move. */
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
  undo: () => void;
  redo: () => void;
  /** The labels of what can be undone (oldest first) and redone. */
  history: { past: string[]; future: string[] };
  /** Undo back to when only `depth` entries were on the stack — one accepted
   * step at a time, each against the room the last one left. */
  undoTo: (depth: number) => void;
}

/**
 * Fold in-flight edits over canonical state. Deletes hide the clip, upserts
 * patch it — so a dragged clip stays where the user dropped it instead of
 * snapping back for the length of a round trip.
 */
function project(
  room: CanonicalRoom,
  pending: Map<string, PendingEdit>,
  scenes: PendingScenes | null,
  cues: PendingCues | null,
  master: PendingMaster | null,
  chains: Map<string, PendingChain>,
): CanonicalRoom {
  if (pending.size === 0 && !scenes && !cues && !master && chains.size === 0) return room;
  const clips = projectClips(room, pending);
  // Scene names ride as one list: shown at once, and — once the room has
  // answered — under the rev it answered with, so a second rename before the
  // broadcast lands cites the right baseRev instead of the stale old one.
  const sceneOverlay = scenes ? { sceneNames: scenes.names, ...(scenes.rev !== null ? { scenesRev: scenes.rev } : {}) } : {};
  const cueOverlay = cues ? { cuePoints: cues.points, ...(cues.rev !== null ? { cuePointsRev: cues.rev } : {}) } : {};
  const masterOverlay = master ? { master: master.level, ...(master.rev !== null ? { masterRev: master.rev } : {}) } : {};
  // Device chains, by lane id or owner: a knob turned right after another
  // (or after an insert) cites the rev the room answered with, not the
  // one the broadcast has yet to bring.
  const tracks =
    chains.size === 0
      ? room.tracks
      : room.tracks.map((t) => {
          const held = chains.get(t.id);
          return held ? { ...t, devices: held.devices, ...(held.rev !== null ? { devicesRev: held.rev } : {}) } : t;
        });
  let owned = room.chains;
  for (const [key, held] of chains) {
    if (!isChainOwner(key)) continue;
    owned = { ...(owned ?? {}), [key]: { devices: held.devices, rev: held.rev ?? room.chains?.[key]?.rev ?? 0 } };
  }
  return { ...room, clips, tracks, ...(owned !== room.chains ? { chains: owned } : {}), ...sceneOverlay, ...cueOverlay, ...masterOverlay };
}

/** A proposed device chain (a knob, an insert, a removal) the room hasn't
 * broadcast back yet, by lane id or chain owner. */
interface PendingChain {
  devices: DeviceState[];
  rev: number | null;
  untilRoomRev: number | null;
}

/** The chain a device intent leaves, from the view it was built against. */
function chainAfter(room: CanonicalRoom, intent: Intent): { key: string; devices: DeviceState[] } | null {
  switch (intent.kind) {
    case "setTrackDevices":
      return { key: intent.id, devices: cloneDevices(intent.devices) };
    case "setChain":
      return { key: intent.owner, devices: cloneDevices(intent.devices) };
    case "setDeviceParam":
    case "setChainParam": {
      const key = intent.kind === "setDeviceParam" ? intent.id : intent.owner;
      const current = intent.kind === "setDeviceParam" ? room.tracks.find((t) => t.id === intent.id)?.devices : room.chains?.[intent.owner]?.devices;
      if (!current) return null;
      const devices = cloneDevices(current);
      const p = devices[intent.device]?.params[intent.param];
      if (!p) return null;
      p.value = intent.value;
      return { key, devices };
    }
    default:
      return null;
  }
}

const DEVICE_INTENT_KINDS = new Set<Intent["kind"]>(["setTrackDevices", "setDeviceParam", "setChain", "setChainParam"]);

/** A proposed master fader the room hasn't broadcast back yet — so a pan
 * turned right after a fader move reads the fader as moved. */
interface PendingMaster {
  level: { volume: number; pan: number };
  rev: number | null;
  untilRoomRev: number | null;
}

/** A proposed locator list the room hasn't broadcast back yet — the scene
 * list's twin. */
interface PendingCues {
  points: Array<{ name: string; time: number }>;
  rev: number | null;
  untilRoomRev: number | null;
}

/** A proposed scene-name list the room hasn't broadcast back yet. */
interface PendingScenes {
  names: Array<string | null>;
  /** The rev the room gave the list; null while in flight. */
  rev: number | null;
  untilRoomRev: number | null;
}

/** Nothing of an undo/redo step landed. */
function refused(res: IntentResponse | null): boolean {
  return !res || (res.accepted.length === 0 && res.rejected.length > 0);
}
/** Whether the same step could land next time: the room did not answer,
 * or every refusal was a stale rev (the re-read refreshes it). */
function retryable(res: IntentResponse | null): boolean {
  return !res || res.rejected.every((r) => r.reason === "stale");
}
export function useJamEditor(
  jamId: string | undefined,
  source: RoomReplica,
  enabled: boolean,
): JamEditor {
  const [pending, setPending] = useState<Map<string, PendingEdit>>(new Map());
  const [pendingScenes, setPendingScenes] = useState<PendingScenes | null>(null);
  const [pendingCues, setPendingCues] = useState<PendingCues | null>(null);
  const [pendingMaster, setPendingMaster] = useState<PendingMaster | null>(null);
  const [pendingChains, setPendingChains] = useState<Map<string, PendingChain>>(new Map());
  /** A multi-step undo in progress (`undoTo`): the stack depth to reach. */
  const [undoTarget, setUndoTarget] = useState<number | null>(null);
  /** ⌘Z / ⇧⌘Z pressed while an edit was still in flight: carried out, in
   * order, once the room has settled — a keypress is never swallowed. */
  const [queued, setQueued] = useState<Array<"undo" | "redo">>([]);
  const [rejections, setRejections] = useState<RejectionNote[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clientId = useRef(`viz_${Math.random().toString(36).slice(2, 10)}`);
  /** Sends go out in order, one at a time (`rebaseIntents`). */
  const sendSeq = useRef(0);
  const sendChain = useRef<Promise<unknown>>(Promise.resolve());
  const inFlight = useRef(0);
  /** Clip id → the rev the room last gave it in one of OUR responses. */
  const knownRevs = useRef(new Map<string, number>());
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
  const [redoStack, setRedoStack] = useState<UndoEntry[]>([]);
  /** The roomRev the room answered our last accepted proposal with. Creates
   * carry no overlay, so this — not the overlay map — says whether the
   * replica has caught up to what we did. */
  const [lastRoomRev, setLastRoomRev] = useState(0);

  const appliedRoomRev = source.replica.appliedRoomRev;

  // Retire overlay entries the replica has caught up to. Because the room
  // broadcasts to the proposer too, the confirmed clip and the removal of its
  // overlay land in the same render — so there is no frame where the clip is
  // drawn at its old position.
  const overlaidRoom = source.replica.room;
  const live = useMemo(() => liveOverlays(pending, appliedRoomRev, overlaidRoom), [pending, appliedRoomRev, overlaidRoom]);

  const liveScenes = useMemo(
    () =>
      pendingScenes && pendingScenes.untilRoomRev !== null && appliedRoomRev >= pendingScenes.untilRoomRev
        ? null
        : pendingScenes,
    [pendingScenes, appliedRoomRev],
  );

  const liveCues = useMemo(
    () => (pendingCues && pendingCues.untilRoomRev !== null && appliedRoomRev >= pendingCues.untilRoomRev ? null : pendingCues),
    [pendingCues, appliedRoomRev],
  );

  const liveMaster = useMemo(
    () => (pendingMaster && pendingMaster.untilRoomRev !== null && appliedRoomRev >= pendingMaster.untilRoomRev ? null : pendingMaster),
    [pendingMaster, appliedRoomRev],
  );

  const liveChains = useMemo(() => {
    const out = new Map<string, PendingChain>();
    for (const [key, held] of pendingChains) {
      if (held.untilRoomRev !== null && appliedRoomRev >= held.untilRoomRev) continue;
      out.set(key, held);
    }
    return out.size === pendingChains.size ? pendingChains : out;
  }, [pendingChains, appliedRoomRev]);

  const room = useMemo(
    () => (source.room ? project(source.room, live, liveScenes, liveCues, liveMaster, liveChains) : null),
    [source.room, live, liveScenes, liveCues, liveMaster, liveChains],
  );
  // The view a proposal is built from, readable inside `propose` without
  // making it a new function every render.
  const roomRef = useRef(room);
  roomRef.current = room;

  const send = useCallback(
    async (raw: Intent[], history: History, reborn?: Map<string, string>): Promise<IntentResponse | null> => {
      if (!jamId || !enabled) return null;
      // A fresh edit (or a redo) ends a running multi-step undo: the target
      // was about the stack as it stood, not about what comes next.
      if (history === "push" || history === "redo") setUndoTarget(null);
      // A fresh edit also drops undos still waiting their turn: they were
      // about what came before it.
      if (history === "push") setQueued((q) => (q.length === 0 ? q : []));
      const intents = coalesceIntents(raw);
      if (intents.length === 0) return null;
      // Captured BEFORE the await: the inverse is "what these displaced", and
      // the replica may have folded the accepted ops in by the time it answers.
      const before = roomRef.current;

      const seq = ++sendSeq.current;
      const overlays = overlaysOf(intents, before);
      setPending((prev) => holdOverlays(prev, overlays, seq));
      const sceneIntent = intents.filter((i): i is Extract<Intent, { kind: "setSceneNames" }> => i.kind === "setSceneNames").at(-1);
      if (sceneIntent) setPendingScenes({ names: [...sceneIntent.names], rev: null, untilRoomRev: null });
      const cueIntent = intents.filter((i): i is Extract<Intent, { kind: "setCuePoints" }> => i.kind === "setCuePoints").at(-1);
      if (cueIntent) setPendingCues({ points: cueIntent.points.map((c) => ({ ...c })), rev: null, untilRoomRev: null });
      const masterIntent = intents.filter((i): i is Extract<Intent, { kind: "setMaster" }> => i.kind === "setMaster").at(-1);
      if (masterIntent) setPendingMaster({ level: { volume: masterIntent.volume, pan: masterIntent.pan }, rev: null, untilRoomRev: null });
      const chainIntents = intents.filter((i) => DEVICE_INTENT_KINDS.has(i.kind));
      // Computed HERE, not inside the state updater: React may run an updater
      // late (or twice), and the keys are needed below when the response
      // lands, whichever wins.
      const chainAfters = before ? chainIntents.flatMap((intent) => chainAfter(before, intent) ?? []) : [];
      const chainKeys = new Set(chainAfters.map((a) => a.key));
      if (chainAfters.length > 0) {
        setPendingChains((prev) => {
          const next = new Map(prev);
          for (const after of chainAfters) next.set(after.key, { devices: after.devices, rev: null, untilRoomRev: null });
          return next;
        });
      }

      setBusy(++inFlight.current > 0);
      // Wait for the send before this one: its answer is what this one's
      // clip revs are rebased onto.
      const turn = sendChain.current;
      let release: () => void = () => undefined;
      sendChain.current = new Promise<void>((r) => (release = r));
      await turn;
      const envelope: IntentEnvelope = {
        v: 3,
        clientId: clientId.current,
        baseRoomRev: source.replica.appliedRoomRev,
        intents: rebaseIntents(intents, knownRevs.current),
      };

      try {
        const result = await postIntents(jamId, envelope);
        for (const op of result.accepted) {
          if (op.kind === "clipUpserted") knownRevs.current.set(op.id, op.rev);
          else if (op.kind === "clipDeleted") knownRevs.current.delete(op.id);
        }
        setPending((prev) => answerOverlays(prev, overlays, seq, result));

        if (sceneIntent) {
          const refusedScenes = result.rejected.some((r) => r.intent.kind === "setSceneNames");
          setPendingScenes((held) =>
            !held || refusedScenes ? null : { ...held, rev: result.roomRev, untilRoomRev: result.roomRev },
          );
        }
        if (cueIntent) {
          const refusedCues = result.rejected.some((r) => r.intent.kind === "setCuePoints");
          setPendingCues((held) => (!held || refusedCues ? null : { ...held, rev: result.roomRev, untilRoomRev: result.roomRev }));
        }
        if (masterIntent) {
          const refusedMaster = result.rejected.some((r) => r.intent.kind === "setMaster");
          setPendingMaster((held) => (!held || refusedMaster ? null : { ...held, rev: result.roomRev, untilRoomRev: result.roomRev }));
        }
        if (chainKeys.size > 0) {
          const refusedKeys = new Set(
            result.rejected.map((r) => chainAfter(before ?? roomRef.current ?? source.room!, r.intent)?.key).filter((k): k is string => k !== undefined),
          );
          setPendingChains((prev) => {
            const next = new Map(prev);
            for (const key of chainKeys) {
              const held = next.get(key);
              if (!held) continue;
              if (refusedKeys.has(key)) next.delete(key);
              else next.set(key, { ...held, rev: result.roomRev, untilRoomRev: result.roomRev });
            }
            return next;
          });
        }
        if (result.accepted.length > 0) setLastRoomRev((r) => Math.max(r, result.roomRev));
        // What this undo/redo brought back came back under new ids: re-point
        // the rest of the history at them, or its entries name clips that
        // are "gone" and the next undo skips to an older action.
        const ids = reborn ? rebornIds(reborn, result.accepted) : null;
        if (ids && ids.size > 0) {
          setUndoStack((prev) => prev.map((e) => remapEntry(e, ids)));
          setRedoStack((prev) => prev.map((e) => remapEntry(e, ids)));
        }
        if (before) {
          const entry = recordUndo(before, result.accepted, describeIntents(intents));
          if (entry) {
            if (history === "push") {
              setUndoStack((prev) => [...prev, entry].slice(-MAX_HISTORY));
              setRedoStack([]);
            } else if (history === "undo") {
              setRedoStack((prev) => [...prev, entry].slice(-MAX_HISTORY));
            } else if (history === "redo") {
              setUndoStack((prev) => [...prev, entry].slice(-MAX_HISTORY));
            }
          }
        }

        if (result.rejected.length > 0) {
          const at = new Date().toISOString();
          setRejections((prev) => [...result.rejected.map((r) => ({ ...r, at })), ...prev].slice(0, 20));
          // Stale or unknown means our view is behind the room's, not that the
          // gesture was wrong. Re-read; the user can repeat it against the truth.
          if (source.replica.onRejections(result.rejected).needsFullState) source.refresh();
        }
        setError(null);
        return result;
      } catch (e: unknown) {
        // The room never saw it, or never answered. Either way our optimistic
        // picture is a guess we can't defend — drop it and show the room's.
        setPending((prev) => dropOverlays(prev, overlays, seq));
        if (sceneIntent) setPendingScenes(null);
        if (cueIntent) setPendingCues(null);
        if (masterIntent) setPendingMaster(null);
        if (chainKeys.size > 0) {
          setPendingChains((prev) => {
            const next = new Map(prev);
            for (const key of chainKeys) next.delete(key);
            return next;
          });
        }
        setError(e instanceof Error ? e.message : String(e));
        source.refresh();
        return null;
      } finally {
        release();
        setBusy(--inFlight.current > 0);
      }
    },
    [jamId, enabled, source],
  );

  const propose = useCallback((intents: Intent[]) => send(intents, "push"), [send]);
  const proposeUnrecorded = useCallback((intents: Intent[]) => send(intents, "none"), [send]);

  // Revs come from the CANONICAL replica, never the projection: an in-flight
  // edit's clip carries the rev it was proposed against, which the room has
  // since moved. Hence the "settled" gate below.
  const settled =
    !busy && live.size === 0 && liveScenes === null && liveCues === null && liveMaster === null && liveChains.size === 0 && appliedRoomRev >= lastRoomRev;
  const undo = useCallback(() => {
    if (!source.room) return;
    if (!settled) {
      setQueued((q) => (q.length < MAX_QUEUED ? [...q, "undo"] : q));
      return;
    }
    // An entry that can no longer be expressed — its clip gone, an audio
    // clip that never got its object — is dropped rather than left on top
    // where it would block every older entry for the rest of the session.
    let stack = undoStack;
    let entry = stack[stack.length - 1];
    let plan: ReturnType<typeof undoPlan> = { intents: [], reborn: new Map() };
    while (entry) {
      plan = undoPlan(source.room, entry);
      if (plan.intents.length > 0) break;
      stack = stack.slice(0, -1);
      entry = stack[stack.length - 1];
    }
    if (!entry) {
      if (stack.length !== undoStack.length) setUndoStack(stack);
      return;
    }
    const taken = entry;
    setUndoStack(stack.slice(0, -1));
    void send(plan.intents, "undo", plan.reborn).then((res) => {
      if (!refused(res)) return;
      // A refused step ends a multi-step run: the effect below would
      // otherwise retry the same inverse against the same room forever.
      setUndoTarget(null);
      // Back on the stack only when a retry can land — no answer, or a
      // stale rev the re-read has since refreshed. An overlap, a missing
      // object or an unknown entity refuses the same way every time.
      if (retryable(res)) setUndoStack((prev) => [...prev, taken].slice(-MAX_HISTORY));
    });
  }, [undoStack, settled, source.room, send]);
  // A multi-step undo is a sequence of single ones: each waits for the room
  // to settle (the next inverse cites revs the previous one moved).
  useEffect(() => {
    if (undoTarget === null) return;
    if (undoStack.length <= undoTarget) {
      setUndoTarget(null);
      return;
    }
    if (settled) undo();
  }, [undoTarget, undoStack.length, settled, undo]);
  const undoTo = useCallback((depth: number) => setUndoTarget(Math.max(0, depth)), []);
  const redo = useCallback(() => {
    if (!source.room) return;
    if (!settled) {
      setQueued((q) => (q.length < MAX_QUEUED ? [...q, "redo"] : q));
      return;
    }
    let stack = redoStack;
    let entry = stack[stack.length - 1];
    let plan: ReturnType<typeof undoPlan> = { intents: [], reborn: new Map() };
    while (entry) {
      plan = undoPlan(source.room, entry);
      if (plan.intents.length > 0) break;
      stack = stack.slice(0, -1);
      entry = stack[stack.length - 1];
    }
    if (!entry) {
      if (stack.length !== redoStack.length) setRedoStack(stack);
      return;
    }
    const taken = entry;
    setRedoStack(stack.slice(0, -1));
    void send(plan.intents, "redo", plan.reborn).then((res) => {
      if (refused(res) && retryable(res)) setRedoStack((prev) => [...prev, taken].slice(-MAX_HISTORY));
    });
  }, [redoStack, settled, source.room, send]);

  useEffect(() => {
    if (queued.length === 0 || !settled) return;
    const next = queued[0]!;
    setQueued((q) => q.slice(1));
    if (next === "undo") undo();
    else redo();
  }, [queued, settled, undo, redo]);

  return {
    room,
    pendingClipIds: useMemo(() => new Set(live.keys()), [live]),
    rejections,
    dismissRejections: useCallback(() => {
      setRejections([]);
      setError(null);
    }, []),
    busy,
    error,
    propose,
    proposeUnrecorded,
    canUndo: settled && undoStack.length > 0,
    canRedo: settled && redoStack.length > 0,
    undoLabel: undoStack[undoStack.length - 1]?.label ?? null,
    redoLabel: redoStack[redoStack.length - 1]?.label ?? null,
    undo,
    redo,
    history: useMemo(
      () => ({ past: undoStack.map((e) => e.label), future: [...redoStack].reverse().map((e) => e.label) }),
      [undoStack, redoStack],
    ),
    undoTo,
  };
}
